import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { FormatterCell, GenericColumnModel } from "./models";
import { columnMinWidth } from "./consts";
import { getColumnMaxInitialWidth } from "./helpers";

export type ColumnWidthMap = { [columnId: string]: number }

export const useColumnWidths = (columns: GenericColumnModel[], rowsData: any[], containerRef: React.MutableRefObject<HTMLDivElement | null>, fill: boolean | undefined) => {
	const [containerWidth, setContainerWidth] = useState<number>(containerRef.current?.offsetWidth || 0);
	const [columnWidthMap, setColumnWidthMap] = useState<ColumnWidthMap>({});

	const calculateColumnWidthsCallback = useCallback(
		() => {
			const container = containerRef.current;

			if (!container) {
				return;
			}

			const maxColumnWidthMap: ColumnWidthMap = {};

			for (let i = 0; i < columns.length; i++) {
				const column = columns[i];
				const columnId = column.id;

				// if width is set, use it directly
				if (column.width) {
					maxColumnWidthMap[columnId] = column.width;
				} else {
					// if width is not set, get all the cells that belong to this column and calculate which cell width is the biggest
					const innerCells = container.querySelectorAll(`[data-columnid="${columnId}"]`);
					const maxInitialWidth = getColumnMaxInitialWidth(column);

					maxColumnWidthMap[columnId] = columnMinWidth;

					for (let j = 0; j < innerCells.length; j++) {
						// calculate cellWidth as content width + cell horizontal padding
						const innerCell = innerCells[j];
						const cell = innerCell.closest('[data-type="cell"]')!;
						const isBodyCell = !!innerCell.closest('[data-body="true"]');
						const paddingLeft = window.getComputedStyle(cell, null).getPropertyValue('padding-left');
						const paddingRight = window.getComputedStyle(cell, null).getPropertyValue('padding-right');
						let cellWidth = innerCell.getBoundingClientRect().width + parseInt(paddingLeft) + parseInt(paddingRight);

						// if cell is editable it contains control that can have some icon, so we leave space for icon
						if (isBodyCell && column.editable) {
							const formatterCell: FormatterCell = {
								rowData: rowsData[j - 1], // - 1 because innerCells include one header cell (header, body, footer)
								value: rowsData[j - 1][columnId],
								columnId
							}
							const isEditable = column.editable(formatterCell);

							if (isEditable) {
								// CODE: random number because of arrow/time_icon/date_icon/some_paddings, and it shouldn't be hardcoded here
								cellWidth += 28;
							}
						}

						// if cellWidth is bigger than maxInitialWidth, then calculation is finished and we use maxInitialWidth value as Column width
						if (cellWidth >= maxInitialWidth) {
							maxColumnWidthMap[columnId] = maxInitialWidth;
							break;
						}

						if (cellWidth > maxColumnWidthMap[columnId]) {
							maxColumnWidthMap[columnId] = Math.ceil(cellWidth);
						}
					}
				}
			}

			const totalColumnWidth = columns.reduce((total, column) => total + maxColumnWidthMap[column.id], 0);

			// if fill === true and there is more empty space on the right, increase column widths so there is no empty space
			if (fill && totalColumnWidth < containerWidth) {
				// CODE: when fill and table is smaller than container,
				// the last column resize line is out of the bounds and horizontal scroll appears,
				// so we add -3 which represents half width of resize handle
				const extraSpace = containerWidth - totalColumnWidth - 3;
				// increase column widths equally, except ones that have width set (GenericColumnModel.width)
				const affectedColumns = columns.filter((column) => !column.width);
				for (const column of affectedColumns) {
					const columnId = column.id;
					maxColumnWidthMap[columnId] = maxColumnWidthMap[columnId] + Math.floor(extraSpace / affectedColumns.length);
				}
			}

			setColumnWidthMap(maxColumnWidthMap);
		},
		[containerRef, columns, fill, containerWidth, rowsData]
	)

	useEffect(
		() => {
			const container = containerRef.current;

			if (!container) {
				return;
			}

			const resizeObserver = new ResizeObserver(() => {
				setContainerWidth(container.clientWidth);
			});

			resizeObserver.observe(container);

			return () => {
				resizeObserver.disconnect();
			};
		},
		[containerRef]
	);

	useLayoutEffect(
		() => {
			calculateColumnWidthsCallback();
		},
		[calculateColumnWidthsCallback]
	)

	return {
		columnWidthMap,
		setColumnWidthMap
	}
}
